import { MakeLogicType, actions, afterMount, connect, kea, listeners, path, reducers, selectors } from 'kea'
import { loaders } from 'kea-loaders'
import { router } from 'kea-router'
import type { LocationChangedPayload } from 'kea-router/lib/types'

import api from 'lib/api'
import { FEATURE_FLAGS } from 'lib/constants'
import { dayjs } from 'lib/dayjs'
import { lemonToast } from 'lib/lemon-ui/LemonToast'
import { featureFlagLogic } from 'lib/logic/featureFlagLogic'
import { newInternalTab } from 'lib/utils/newInternalTab'
import { organizationLogic } from 'scenes/organizationLogic'
import { preflightLogic } from 'scenes/PreflightCheck/preflightLogic'
import { sceneLogic } from 'scenes/sceneLogic'
import { aiConsentLogic } from 'scenes/settings/organization/aiConsentLogic'
import { teamLogic } from 'scenes/teamLogic'
import { urls } from 'scenes/urls'

import { sidePanelStateLogic } from '~/layout/navigation-3000/sidepanel/sidePanelStateLogic'
import { Conversation, ConversationDetail, SidePanelTab } from '~/types'

import { conversationsDestroy } from 'products/posthog_ai/frontend/generated/api'

import type { FeatureFlagsSet } from '../../lib/logic/featureFlagLogic'
import type { OrganizationType, PreflightStatus } from '../../types'
import type { SceneConfig } from '../sceneTypes'
import { TOOL_DEFINITIONS, ToolRegistration } from './max-constants'
import { PHAI_VIEW_MODE_KEY } from './max-storage-keys'
import { SIDE_PANEL_PANEL_ID, maxLogic, mergeConversationHistory, mergeConversations } from './maxLogic'

/**
 * Which PostHog AI implementation the Max scene renders. `'new'` is the posthog_ai/frontend surface
 * (the tasks product on `/ai`, the new composer + thread viewer in the side panel); `'legacy'` is the
 * existing Max chat. Only meaningful while the `PHAI_SANDBOX_MODE` flag is on — see `effectivePhaiView`.
 */
export type PhaiViewMode = 'new' | 'legacy'

// Keep this stored across all projects, only display this once per device
const AI_LIABILITY_NOTICE_STORAGE_KEY = 'posthog_ai_liability_notice_dismissed'

/** Tools available everywhere. These CAN be shadowed by contextual tools for scene-specific handling (e.g. to intercept insight creation). */
export const STATIC_TOOLS: ToolRegistration[] = [
    {
        identifier: 'filter_session_recordings' as const,
        name: TOOL_DEFINITIONS['filter_session_recordings'].name,
        description: TOOL_DEFINITIONS['filter_session_recordings'].description,
    },
    {
        identifier: 'web_search',
        name: TOOL_DEFINITIONS['web_search'].name,
        description: TOOL_DEFINITIONS['web_search'].description,
    },
    {
        identifier: 'search' as const,
        name: TOOL_DEFINITIONS['search'].name,
        description: TOOL_DEFINITIONS['search'].description,
    },
    {
        identifier: 'create_task' as const,
        name: TOOL_DEFINITIONS['create_task'].name,
        description: TOOL_DEFINITIONS['create_task'].description,
    },
    {
        identifier: 'run_task' as const,
        name: TOOL_DEFINITIONS['run_task'].name,
        description: TOOL_DEFINITIONS['run_task'].description,
    },
    {
        identifier: 'get_task_run' as const,
        name: TOOL_DEFINITIONS['get_task_run'].name,
        description: TOOL_DEFINITIONS['get_task_run'].description,
    },
    {
        identifier: 'get_task_run_logs' as const,
        name: TOOL_DEFINITIONS['get_task_run_logs'].name,
        description: TOOL_DEFINITIONS['get_task_run_logs'].description,
    },
    {
        identifier: 'list_tasks' as const,
        name: TOOL_DEFINITIONS['list_tasks'].name,
        description: TOOL_DEFINITIONS['list_tasks'].description,
    },
    {
        identifier: 'list_task_runs' as const,
        name: TOOL_DEFINITIONS['list_task_runs'].name,
        description: TOOL_DEFINITIONS['list_task_runs'].description,
    },
]

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface maxGlobalLogicValues {
    aiAccessRequested: boolean // aiConsentLogic
    dataProcessingAccepted: boolean // aiConsentLogic
    dataProcessingApprovalDisabledReason: string | null // aiConsentLogic
    dataProcessingDismissed: boolean // aiConsentLogic
    requestingAiAccess: boolean // aiConsentLogic
    featureFlags: FeatureFlagsSet // featureFlagLogic
    currentOrganization: OrganizationType | null // organizationLogic
    isCloudOrDev: boolean | undefined // preflightLogic
    preflight: PreflightStatus | null // preflightLogic
    sceneConfig: SceneConfig | null // sceneLogic
    sceneId: string | null // sceneLogic
    selectedTab: SidePanelTab | null // sidePanelStateLogic
    sidePanelOpen: boolean // sidePanelStateLogic
    currentTeamIdStrict: number | string // teamLogic
    availableStaticTools: ToolRegistration[]
    conversationHistory: ConversationDetail[]
    conversationHistoryLoading: boolean
    currentConversationId: string | null
    editInsightToolRegistered: true
    effectivePhaiView: PhaiViewMode
    isMaxAvailable: boolean
    isOrganizationCreatedRecently: boolean
    isPhaiSandboxFlagOn: boolean
    liabilityNoticeDismissed: boolean
    phaiViewMode: PhaiViewMode
    registeredToolMap: Record<string, ToolRegistration>
    shouldShowLiabilityNotice: boolean
    toolMap: {
        [x: string]: ToolRegistration
    }
    toolSuggestions: string[]
    tools: ToolRegistration[]
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface maxGlobalLogicActions {
    acceptDataProcessing: (
        testOnlyOverride?: boolean | undefined,
        resumeUrl?: string | undefined
    ) => {
        resumeUrl: string | undefined
        testOnlyOverride: boolean | undefined
    } // aiConsentLogic
    dismissDataProcessing: () => {
        value: true
    } // aiConsentLogic
    requestAiAccess: () => {
        value: true
    } // aiConsentLogic
    locationChanged: ({
        method,
        pathname,
        search,
        searchParams,
        hash,
        hashParams,
        initial,
        url,
        routerState,
    }: LocationChangedPayload) => {
        hash: string
        hashParams: Record<string, any>
        initial: boolean
        method: 'POP' | 'PUSH' | 'REPLACE'
        pathname: string
        routerState: Record<string, any>
        search: string
        searchParams: Record<string, any>
        url: string
    } // router
    openSidePanel: (
        tab: SidePanelTab,
        options?: string | undefined
    ) => {
        options: string | undefined
        tab: SidePanelTab
    } // sidePanelStateLogic
    askSidePanelMax: (prompt: string) => {
        prompt: string
    }
    deleteConversation: (id: string) => {
        id: string
    }
    deregisterTool: (key: string) => {
        key: string
    }
    dismissLiabilityNotice: () => {
        value: true
    }
    loadConversation: (conversationId: string) => string
    loadConversationFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadConversationHistory: (_?: {
        /** If true, the current thread will not be updated with the retrieved conversation. */
        doNotUpdateCurrentThread?: boolean
    }) => {
        doNotUpdateCurrentThread?: boolean
    }
    loadConversationHistoryFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadConversationHistorySuccess: (
        conversationHistory: ConversationDetail[],
        payload?: {
            doNotUpdateCurrentThread?: boolean
        }
    ) => {
        conversationHistory: ConversationDetail[]
        payload?: {
            doNotUpdateCurrentThread?: boolean
        }
    }
    loadConversationSuccess: (
        conversationHistory: ConversationDetail[],
        payload?: string
    ) => {
        conversationHistory: ConversationDetail[]
        payload?: string
    }
    openSidePanelMax: (conversationId?: string) => {
        conversationId: string | undefined
    }
    openSidePanelMaxWithTaskBind: (taskId: string) => {
        taskId: string
    }
    prependOrReplaceConversation: (conversation: Conversation | ConversationDetail) => {
        conversation: Conversation | ConversationDetail
    }
    registerTool: (tool: ToolRegistration) => {
        tool: ToolRegistration
    }
    setPhaiViewMode: (mode: PhaiViewMode) => {
        mode: PhaiViewMode
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface maxGlobalLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        currentConversationId: (searchParams: Record<string, any>) => string | null
        isMaxAvailable: (isCloudOrDev: boolean | undefined, preflight: PreflightStatus | null) => boolean
        isOrganizationCreatedRecently: (currentOrganization: OrganizationType | null) => boolean
        shouldShowLiabilityNotice: (
            isOrganizationCreatedRecently: boolean,
            liabilityNoticeDismissed: boolean
        ) => boolean
        isPhaiSandboxFlagOn: (featureFlags: FeatureFlagsSet) => boolean
        effectivePhaiView: (isPhaiSandboxFlagOn: boolean, phaiViewMode: PhaiViewMode) => PhaiViewMode
        availableStaticTools: (featureFlags: FeatureFlagsSet) => ToolRegistration[]
        toolMap: (
            registeredToolMap: Record<string, ToolRegistration>,
            availableStaticTools: ToolRegistration[]
        ) => {
            [x: string]: ToolRegistration
        }
        tools: (toolMap: { [x: string]: ToolRegistration }) => ToolRegistration[]
        editInsightToolRegistered: (registeredToolMap: Record<string, ToolRegistration>) => true
        toolSuggestions: (tools: ToolRegistration[]) => string[]
    }
}

export type maxGlobalLogicType = MakeLogicType<
    maxGlobalLogicValues,
    maxGlobalLogicActions,
    Record<string, any>,
    maxGlobalLogicMeta
>

export const maxGlobalLogic = kea<maxGlobalLogicType>([
    path(['scenes', 'max', 'maxGlobalLogic']),
    connect(() => ({
        values: [
            organizationLogic,
            ['currentOrganization'],
            sceneLogic,
            ['sceneId', 'sceneConfig'],
            teamLogic,
            ['currentTeamIdStrict'],
            featureFlagLogic,
            ['featureFlags'],
            sidePanelStateLogic,
            ['sidePanelOpen', 'selectedTab'],
            preflightLogic,
            ['preflight', 'isCloudOrDev'],
            aiConsentLogic,
            [
                'dataProcessingAccepted',
                'dataProcessingDismissed',
                'dataProcessingApprovalDisabledReason',
                'aiAccessRequested',
                'requestingAiAccess',
            ],
        ],
        actions: [
            router,
            ['locationChanged'],
            sidePanelStateLogic,
            ['openSidePanel'],
            aiConsentLogic,
            ['acceptDataProcessing', 'dismissDataProcessing', 'requestAiAccess'],
        ],
    })),
    actions({
        openSidePanelMax: (conversationId?: string) => ({ conversationId }),
        openSidePanelMaxWithTaskBind: (taskId: string) => ({ taskId }),
        askSidePanelMax: (prompt: string) => ({ prompt }),
        registerTool: (tool: ToolRegistration) => ({ tool }),
        deregisterTool: (key: string) => ({ key }),
        prependOrReplaceConversation: (conversation: ConversationDetail | Conversation) => ({ conversation }),
        deleteConversation: (id: string) => ({ id }),
        dismissLiabilityNotice: true,
        setPhaiViewMode: (mode: PhaiViewMode) => ({ mode }),
    }),

    loaders(({ values }) => ({
        conversationHistory: [
            [] as ConversationDetail[],
            {
                loadConversationHistory: async (
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used for conversation restoration
                    _?: {
                        /** If true, the current thread will not be updated with the retrieved conversation. */
                        doNotUpdateCurrentThread?: boolean
                    }
                ) => {
                    const response = await api.conversations.list()
                    return response.results.map((conversation) =>
                        mergeConversations(
                            conversation,
                            values.conversationHistory.find((existing) => existing.id === conversation.id)
                        )
                    )
                },

                loadConversation: async (conversationId: string) => {
                    const response = await api.conversations.get(conversationId)
                    if (!response) {
                        // The endpoint can return an empty body; a null in the history crashes consumers
                        return values.conversationHistory
                    }
                    const itemIndex = values.conversationHistory.findIndex((c) => c.id === conversationId)

                    if (itemIndex !== -1) {
                        return [
                            ...values.conversationHistory.slice(0, itemIndex),
                            response,
                            ...values.conversationHistory.slice(itemIndex + 1),
                        ]
                    }
                    return [response, ...values.conversationHistory]
                },
            },
        ],
    })),

    reducers({
        conversationHistory: {
            prependOrReplaceConversation: (state, { conversation }) => {
                return mergeConversationHistory(state, conversation)
            },
        },
        registeredToolMap: [
            {} as Record<string, ToolRegistration>,
            {
                registerTool: (state, { tool }) => ({
                    ...state,
                    [tool.identifier]: tool,
                }),
                deregisterTool: (state, { key }) => {
                    const newState = { ...state }
                    delete newState[key]
                    return newState
                },
            },
        ],
        liabilityNoticeDismissed: [
            false,
            { persist: true, storageKey: AI_LIABILITY_NOTICE_STORAGE_KEY },
            {
                dismissLiabilityNotice: () => true,
            },
        ],
        // The user's chosen implementation, persisted per device. Defaults to the new surface so a
        // flagged user lands there; `effectivePhaiView` collapses this to legacy when the flag is off.
        phaiViewMode: [
            'new' as PhaiViewMode,
            { persist: true, storageKey: PHAI_VIEW_MODE_KEY },
            {
                setPhaiViewMode: (_, { mode }) => mode,
            },
        ],
    }),
    listeners(({ actions, values }) => ({
        askSidePanelMax: ({ prompt }) => {
            newInternalTab(urls.ai(undefined, prompt))
        },
        openSidePanelMax: ({ conversationId }) => {
            if (!values.sidePanelOpen || values.selectedTab !== SidePanelTab.Max) {
                actions.openSidePanel(SidePanelTab.Max)
            }
            if (conversationId) {
                let logic = maxLogic.findMounted({ panelId: SIDE_PANEL_PANEL_ID })
                if (!logic) {
                    logic = maxLogic({ panelId: SIDE_PANEL_PANEL_ID })
                    logic.mount() // we're never unmounting this
                }
                logic.actions.openConversation(conversationId)
            }
        },
        // Open the side panel on a fresh chat bound to a sandbox Task (inbox "Open task" — in-place,
        // not a new tab). The side panel doesn't sync the URL, so the binding is seeded directly here
        // rather than via the `bind_task` param the scene route reads. `setPendingBindTaskId` runs
        // after `startNewConversation`, which clears it.
        openSidePanelMaxWithTaskBind: ({ taskId }) => {
            if (!values.sidePanelOpen || values.selectedTab !== SidePanelTab.Max) {
                actions.openSidePanel(SidePanelTab.Max)
            }
            let logic = maxLogic.findMounted({ panelId: SIDE_PANEL_PANEL_ID })
            if (!logic) {
                logic = maxLogic({ panelId: SIDE_PANEL_PANEL_ID })
                logic.mount() // we're never unmounting this
            }
            logic.actions.startNewConversation()
            logic.actions.setPendingBindTaskId(taskId)
        },
        loadConversationHistoryFailure: ({ errorObject }) => {
            lemonToast.error(errorObject?.data?.detail || 'Failed to load conversation history.')
        },
        deleteConversation: async ({ id }) => {
            try {
                await conversationsDestroy(String(values.currentTeamIdStrict), id)
                if (values.currentConversationId === id) {
                    router.actions.push(urls.aiHistory())
                }
                for (const logic of maxLogic.findAllMounted()) {
                    if (logic.values.conversationId === id) {
                        logic.actions.startNewConversation()
                    }
                }
                actions.loadConversationHistory()
            } catch {
                lemonToast.error('Failed to delete chat')
            }
        },
    })),
    afterMount(({ actions }) => {
        actions.loadConversationHistory()
    }),

    selectors({
        currentConversationId: [
            () => [router.selectors.searchParams],
            (searchParams: Record<string, any>): string | null => searchParams?.chat ?? null,
        ],
        // On Cloud/dev a provider key is always present. On a self-hosted (hobby) instance Max only
        // works once ANTHROPIC_API_KEY is configured, so we surface a "set the key" state instead.
        // Treat a not-yet-loaded preflight (null) as available so the empty-state doesn't flash
        // before preflight resolves — once loaded we gate on cloud/dev or the key being present.
        isMaxAvailable: [
            (s) => [s.isCloudOrDev, s.preflight],
            (isCloudOrDev: boolean | undefined, preflight: null | import('~/types').PreflightStatus): boolean =>
                !preflight || !!isCloudOrDev || !!preflight.anthropic_available,
        ],
        isOrganizationCreatedRecently: [
            (s) => [s.currentOrganization],
            (currentOrganization: null | import('~/types').OrganizationType): boolean => {
                const orgCreatedAt = currentOrganization?.created_at
                return orgCreatedAt ? dayjs().diff(dayjs(orgCreatedAt), 'day') <= 15 : false
            },
        ],
        shouldShowLiabilityNotice: [
            (s) => [s.isOrganizationCreatedRecently, s.liabilityNoticeDismissed],
            (isOrganizationCreatedRecently: boolean, liabilityNoticeDismissed: boolean): boolean =>
                isOrganizationCreatedRecently && !liabilityNoticeDismissed,
        ],
        // Gates the new-vs-legacy toggle: only flagged users can switch, and the new surface only
        // exists for them.
        isPhaiSandboxFlagOn: [
            (s) => [s.featureFlags],
            (featureFlags: import('lib/logic/featureFlagLogic').FeatureFlagsSet): boolean =>
                !!featureFlags[FEATURE_FLAGS.PHAI_SANDBOX_MODE],
        ],
        // The implementation the Max scene actually renders: the user's choice while the flag is on,
        // always legacy otherwise (so non-flagged users are unaffected regardless of any stored value).
        effectivePhaiView: [
            (s) => [s.isPhaiSandboxFlagOn, s.phaiViewMode],
            (isPhaiSandboxFlagOn: boolean, phaiViewMode: PhaiViewMode): PhaiViewMode =>
                isPhaiSandboxFlagOn ? phaiViewMode : 'legacy',
        ],
        availableStaticTools: [
            (s) => [s.featureFlags],
            (featureFlags: import('lib/logic/featureFlagLogic').FeatureFlagsSet): ToolRegistration[] => {
                const staticTools = STATIC_TOOLS.filter((tool) => {
                    // Only register the static tools that either aren't flagged or have their flag enabled
                    const toolDefinition = TOOL_DEFINITIONS[tool.identifier]
                    return !toolDefinition.flag || featureFlags[toolDefinition.flag]
                })
                return staticTools
            },
        ],
        toolMap: [
            (s) => [s.registeredToolMap, s.availableStaticTools],
            (registeredToolMap: Record<string, ToolRegistration>, availableStaticTools: ToolRegistration[]) => ({
                ...Object.fromEntries(availableStaticTools.map((tool) => [tool.identifier, tool])),
                ...registeredToolMap,
            }),
        ],
        tools: [
            (s) => [s.toolMap],
            (toolMap: { [x: string]: ToolRegistration }): ToolRegistration[] => Object.values(toolMap),
        ],
        editInsightToolRegistered: [
            (s) => [s.registeredToolMap],
            (registeredToolMap: Record<string, ToolRegistration>) => !!registeredToolMap.create_insight,
        ],
        toolSuggestions: [
            (s) => [s.tools],
            (tools: ToolRegistration[]): string[] => {
                const suggestions: string[] = []
                for (const tool of tools) {
                    if (tool.suggestions) {
                        suggestions.push(...tool.suggestions)
                    }
                }
                return suggestions
            },
        ],
    }),
])
